import { Pod } from 'kubernetes-types/core/v1';

import { Stack } from '@/react/common/stacks/types';
import { useEnvironmentDeploymentOptions } from '@/react/portainer/environments/queries/useEnvironment';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { isGitConfigDiverged } from '@/react/portainer/gitops/utils';

import { AddButton } from '@@/buttons';

import { useAppStackFile } from '../../queries/useAppStackFile';
import { Application } from '../../types';
import { applicationIsKind } from '../../utils';

import { EditButtons } from './EditButtons';
import { RedeployApplicationButton } from './RedeployApplicationButton';
import { RestartApplicationButton } from './RestartApplicationButton';
import { RollbackApplicationButton } from './RollbackApplicationButton';

export function ButtonsLine({
  stack,
  environmentId,
  externalApp,
  appStackId,
  appStackKind,
  app,
  name,
  namespace,
}: {
  stack?: Stack;
  externalApp: boolean;
  environmentId: EnvironmentId;
  appStackId?: number;
  appStackKind?: string;
  namespace: string;
  app?: Application;
  name: string;
}) {
  const { data: deploymentOptions } =
    useEnvironmentDeploymentOptions(environmentId);
  const hideEditButton = deploymentOptions && deploymentOptions?.hideWebEditor;

  const appStackFileQuery = useAppStackFile(
    {
      id: stack?.Id ?? appStackId,
      kind: appStackKind,
    },
    {
      enabled:
        !!stack &&
        (!stack.GitConfig ||
          !isGitConfigDiverged(stack.GitConfig, stack.CurrentDeploymentInfo)),
    }
  );
  const appStackFileContent = appStackFileQuery.data;

  return (
    <div className="flex flex-wrap gap-2">
      {!hideEditButton && (
        <EditButtons
          isEdge={appStackKind === 'edge'}
          stackId={stack?.Id ?? appStackId}
          externalApp={externalApp}
          stack={stack}
          namespace={namespace}
        />
      )}
      {!applicationIsKind<Pod>('Pod', app) && (
        <>
          <RestartApplicationButton
            environmentId={environmentId}
            namespace={namespace}
            appName={name}
            appKind={app?.kind}
          />
          <RedeployApplicationButton
            environmentId={environmentId}
            namespace={namespace}
            appName={name}
            app={app}
          />
        </>
      )}
      {!externalApp && (
        <RollbackApplicationButton
          environmentId={environmentId}
          namespace={namespace}
          appName={name}
          app={app}
        />
      )}
      {appStackFileContent && !hideEditButton && (
        <AddButton
          to="kubernetes.templates.custom.new"
          data-cy="k8sAppDetail-createCustomTemplateButton"
          params={{
            fileContent: appStackFileContent,
          }}
        >
          Create template from application
        </AddButton>
      )}
    </div>
  );
}
